import React, { useContext, useMemo } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { AuthContext } from '../store/ContextStore';
import localStorageService from '../services/localStorageService';
import { SPORT_ACCESS } from '../constants/routes';

type BlockReason = 'locked' | 'expired' | 'trial_expired';

function getSportRow(subscription: any, sportCode: string): any | null {
  const sportAccess = subscription?.sport_access ?? subscription?.sportAccess;
  if (!sportAccess || typeof sportAccess !== 'object') return null;
  return (sportAccess as any)?.[sportCode] ?? null;
}

function getBlockReason(subscription: any, sportCode: string, nowSec: number): BlockReason | null {
  const row = getSportRow(subscription, sportCode);
  if (!row) return null;

  const status = String(row?.status || '').trim().toLowerCase();
  const untilSec = Number(row?.until);

  if (status === 'locked') return 'locked';
  if (status === 'trial_end') return 'trial_expired';
  if (status === 'trial' && Number.isFinite(untilSec) && untilSec > 0 && untilSec <= nowSec) return 'trial_expired';
  if (['expired', 'inactive'].includes(status)) return 'expired';

  return null;
}

export default function SportAccessGuard({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const params = useParams();
  const auth = useContext(AuthContext);

  const sportCodeFromParams = (params as any)?.sportsCode || (params as any)?.sportCode || null;
  const selectedSport = (auth as any)?.selectedSport || localStorageService.getSelectedSport('');
  const sportCode = String(sportCodeFromParams || selectedSport || '').trim();

  const subscription = useMemo(() => {
    const contextUser = (auth as any)?.user;
    const resolvedUser =
      contextUser && typeof contextUser === 'object' && Object.keys(contextUser).length > 0
        ? contextUser
        : localStorageService.getUserInfo();
    return (resolvedUser as any)?.subscription;
  }, [auth]);

  const reason = useMemo(() => {
    if (!sportCode) return null;
    const nowSec = Math.floor(Date.now() / 1000);
    return getBlockReason(subscription, sportCode, nowSec);
  }, [sportCode, subscription]);

  if (reason) {
    return (
      <Navigate
        to={SPORT_ACCESS}
        replace
        state={{
          sportCode,
          reason,
          from: location.pathname + location.search,
        }}
      />
    );
  }

  return <>{children}</>;
}
